/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Load scoped classroom server state through TanStack Query.
 * @state-owner TanStack Query
 * @allowed-imports same module model; app environment; shared demo data; TanStack Query
 * @forbidden routes; app bootstrap; direct generated SDK; Zustand entity copies
 * @public-api via modules/classrooms/index.ts
 * @side-effects authenticated GET request outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests modules/classrooms/model/next-lesson.test.ts and module-contracts.test.ts
 */

import { useQuery } from '@tanstack/react-query';
import { classFileSchema } from '@vispo/contracts';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import {
  type DemoRunSummary,
  demoActivities,
  demoClasses,
  demoRuns,
} from '../../../shared/lib/demo-adapters';
import {
  type ClassActivitySummary,
  type ClassMemberSummary,
  type ClassResourceSummary,
  type ClassroomSummary,
  type ExistingClassMemberResponse,
  mapExistingClassMemberResponse,
  mapExistingClassResponse,
} from '../model/types';

export const classroomKeys = {
  all: (activeContextId: string) => ['classrooms', activeContextId] as const,
  members: (activeContextId: string, classId: string) =>
    [...classroomKeys.all(activeContextId), classId, 'members'] as const,
  resources: (activeContextId: string, classId: string) =>
    [...classroomKeys.all(activeContextId), classId, 'resources'] as const,
  runs: (activeContextId: string, classId: string, role: 'teacher' | 'student') =>
    [...classroomKeys.all(activeContextId), classId, 'activities', role] as const,
};

const classworkItemSchema = z
  .object({
    id: z.string().min(1),
    item_type: z.enum(['assignment', 'question', 'material', 'quiz']),
    title: z.string().min(1),
    status: z.enum(['draft', 'published', 'archived']).nullable().optional(),
    due_date: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
  })
  .loose();

const studentExamSchema = z
  .object({
    assignment_id: z.string().min(1),
    exam_title: z.string().min(1),
    class_id: z.string().nullable().optional(),
    due_date: z.string().nullable().optional(),
    status: z.string(),
    percentage: z.number().nullable().optional(),
    submitted_at: z.string().nullable().optional(),
  })
  .loose();

export function useClassrooms(activeContextId: string) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: classroomKeys.all(activeContextId),
    queryFn: async (): Promise<ClassroomSummary[]> => {
      if (environment.demoMode) return demoClasses.map((item) => ({ ...item }));
      const response =
        await environment.apiClient.request<
          Array<{
            id: string;
            name: string;
            subject?: string;
            student_count?: number;
            join_code: string;
          }>
        >('/api/classes');
      return response.map(mapExistingClassResponse);
    },
  });
}

export function useClassResources(activeContextId: string, classId: string) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: classroomKeys.resources(activeContextId, classId),
    queryFn: async (): Promise<ClassResourceSummary[]> => {
      if (environment.demoMode) {
        return [
          {
            id: `${classId}-resource-1`,
            name: '一元一次方程式課堂講義.pdf',
            kind: 'document',
            mimeType: 'application/pdf',
            sizeBytes: 1_482_000,
            url: null,
            status: 'ready',
          },
          {
            id: `${classId}-resource-2`,
            name: '第 3 章練習題',
            kind: 'exam',
            mimeType: 'application/pdf',
            sizeBytes: 864_000,
            url: null,
            status: 'ready',
          },
        ];
      }
      const files = z
        .array(classFileSchema)
        .parse(
          await environment.apiClient.request<unknown>(
            `/api/classes/${encodeURIComponent(classId)}/resources`,
          ),
        );
      return files.flatMap((file): ClassResourceSummary[] =>
        file.status === 'deleted'
          ? []
          : [
              {
                id: file.id,
                name: file.name,
                kind: file.file_type,
                mimeType: file.mime_type,
                sizeBytes: file.size_bytes,
                url: file.signed_url,
                status: file.status,
              },
            ],
      );
    },
  });
}

export function useClassRuns(
  activeContextId: string,
  classId: string,
  role: 'teacher' | 'student',
) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: classroomKeys.runs(activeContextId, classId, role),
    queryFn: async (): Promise<ClassActivitySummary[]> => {
      if (environment.demoMode) {
        return demoRuns
          .filter((run: DemoRunSummary) => run.classId === classId)
          .flatMap((run: DemoRunSummary): ClassActivitySummary[] => {
            const activity = demoActivities.find(
              (item: (typeof demoActivities)[number]) => item.id === run.activityId,
            );
            if (!activity) return [];
            return [
              {
                id: activity.id,
                title: activity.title,
                kind: 'quiz',
                status: 'published',
                dueDate: null,
                updatedAt: null,
                progressLabel: run.status === 'live' ? '課堂進行' : '指派作業',
                scoreLabel: run.status === 'live' ? '進行中' : '收件中',
              },
            ];
          });
      }

      if (role === 'student') {
        const rows = z
          .array(studentExamSchema)
          .parse(await environment.apiClient.request<unknown>('/api/students/me/runs'));
        return rows
          .filter((row) => row.class_id === classId)
          .map(
            (row): ClassActivitySummary => ({
              id: row.assignment_id,
              title: row.exam_title,
              kind: 'quiz',
              status: 'published',
              dueDate: row.due_date ?? null,
              updatedAt: row.submitted_at ?? null,
              progressLabel: studentProgressLabel(row.status, row.due_date),
              scoreLabel:
                typeof row.percentage === 'number'
                  ? `${Math.round(row.percentage)}%`
                  : row.status === 'submitted' || row.status === 'graded'
                    ? '已完成'
                    : '待完成',
            }),
          );
      }

      const rows = z
        .array(classworkItemSchema)
        .parse(
          await environment.apiClient.request<unknown>(
            `/api/classes/${encodeURIComponent(classId)}/runs`,
          ),
        );
      return rows.map(
        (row): ClassActivitySummary => ({
          id: row.id,
          title: row.title,
          kind: row.item_type,
          status: row.status ?? 'draft',
          dueDate: row.due_date ?? null,
          updatedAt: row.updated_at ?? null,
          progressLabel:
            row.item_type === 'material'
              ? '課堂教材'
              : row.status === 'published'
                ? '已發布'
                : row.status === 'archived'
                  ? '已封存'
                  : '草稿',
          scoreLabel:
            row.status === 'published' ? '收件中' : row.status === 'archived' ? '已結束' : '草稿',
        }),
      );
    },
  });
}

function studentProgressLabel(status: string, dueDate: string | null | undefined): string {
  if (status === 'submitted' || status === 'graded') return '已完成';
  if (!dueDate) return '待開始';
  const date = new Date(dueDate);
  return Number.isFinite(date.getTime())
    ? `期限 ${new Intl.DateTimeFormat('zh-TW', {
        month: 'numeric',
        day: 'numeric',
      }).format(date)}`
    : '待完成';
}

export function useClassStudents(activeContextId: string, classItem: ClassroomSummary) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: classroomKeys.members(activeContextId, classItem.id),
    queryFn: async (): Promise<ClassMemberSummary[]> => {
      if (environment.demoMode) {
        return classItem.students.map((name, index) => ({
          id: `${classItem.id}-member-${index}`,
          userId: `${classItem.id}-student-${index}`,
          name,
          email: null,
          role: 'student',
          status: 'active',
          lastSeenAt: null,
        }));
      }
      const response = await environment.apiClient.request<ExistingClassMemberResponse[]>(
        `/api/classes/${classItem.id}/students`,
      );
      return response
        .map(mapExistingClassMemberResponse)
        .filter((member) => member.role === 'student' && member.status === 'active');
    },
  });
}
